import { del, get, patch, post } from './client';
import { ROUTES } from './config';
import type { ApiEnvelope, Job } from '@/types/types';

export interface JobFiltersPayload {
    searchTerm?: string;
    sort?: 'asc' | 'desc';
    filters?: Record<string, unknown>;
    quickFilters?: Record<string, unknown>;
    dateFilter?: { start?: number | null; end?: number | null };
    pageSize?: number;
    pageNumber?: number;
}

const toYmd = (ts: number): string =>
    new Date(ts).toISOString().split('T')[0];

const buildJobsParams = (opts: JobFiltersPayload): Record<string, string> => {
    const params: Record<string, string> = {};
    params.ordering = opts.sort ?? 'desc';
    params.page_size = String(opts.pageSize ?? 25);
    if (opts.pageNumber) params.page_number = String(opts.pageNumber);
    if (opts.searchTerm) params.search = opts.searchTerm.split(' ').join(',');
    if (opts.dateFilter?.start) params.start_date = toYmd(opts.dateFilter.start);
    if (opts.dateFilter?.end) params.end_date = toYmd(opts.dateFilter.end);

    if (opts.quickFilters) {
        Object.entries(opts.quickFilters).forEach(([key, value]) => {
            if (value != null && String(value).length) {
                params[key] = String(value);
            }
        });
    }

    if (opts.filters) {
        Object.entries(opts.filters).forEach(([key, value]) => {
            if (Array.isArray(value) && value.length) {
                params[key] = value.join(',');
            } else if (typeof value === 'string' && value.length) {
                params[key] = value;
            }
        });
    }

    return params;
};

export const jobsApi = {
    list: (opts: JobFiltersPayload = {}) =>
        get<ApiEnvelope<Job[]>>(ROUTES.JOBS_LIST, { params: buildJobsParams(opts) }),
    listByNextPageUrl: (nextPageUrl: string) =>
        get<ApiEnvelope<Job[]>>(`/rodx${nextPageUrl}`),
    byId: (id: string) => get<ApiEnvelope<Job>>(ROUTES.JOB(id)),
    create: (payload: Partial<Job>) =>
        post<ApiEnvelope<Job>>(ROUTES.JOB_CREATE, payload),
    update: (id: string, payload: Partial<Job>) =>
        patch<ApiEnvelope<Job>>(ROUTES.JOB_UPDATE(id), payload),
    remove: (id: string) => del<void>(ROUTES.JOB_DELETE(id)),
    count: () => get<ApiEnvelope<number>>(ROUTES.JOB_COUNT),
};
